import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/solanova/Logo";
import {
  ArrowLeft,
  LogOut,
  Plus,
  Save,
  Trash2,
  Eye,
  EyeOff,
  PenSquare,
} from "lucide-react";

function AdminHeader() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <header className="glass-nav fixed top-0 inset-x-0 z-50 py-3">
      <div className="max-w-6xl mx-auto px-6 md:px-10 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="admin-logo-home">
          <Logo size="sm" />
        </Link>
        <div className="flex items-center gap-6">
          <span className="hidden sm:block text-xs text-white/50 font-body">
            Eingeloggt als <span className="text-white/80">{user?.email}</span>
          </span>
          <button
            data-testid="admin-logout"
            onClick={() => {
              logout();
              navigate("/admin/login");
            }}
            className="text-sm text-white/70 hover:text-white inline-flex items-center gap-2 font-body"
          >
            <LogOut size={15} /> Abmelden
          </button>
        </div>
      </div>
    </header>
  );
}

// ----------------- Login -----------------
export function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Willkommen zurück.");
      navigate("/admin");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Anmeldung fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main
      data-testid="admin-login-page"
      className="bg-[var(--sol-bg)] min-h-screen text-white flex items-center justify-center px-6"
    >
      <div className="w-full max-w-md">
        <div className="text-center">
          <Logo size="lg" />
          <p className="eyebrow mt-8">Admin-Bereich</p>
          <h1 className="font-display text-3xl md:text-4xl font-extralight tracking-tight mt-4">
            Anmeldung
          </h1>
        </div>

        <form
          onSubmit={submit}
          className="mt-10 bg-[var(--sol-surface)] border border-white/8 rounded-3xl p-8 space-y-6"
        >
          <div>
            <label className="text-[11px] tracking-[0.22em] uppercase text-white/40">
              E-Mail
            </label>
            <input
              data-testid="admin-login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@solanova.ch"
              className="sol-input"
            />
          </div>
          <div>
            <label className="text-[11px] tracking-[0.22em] uppercase text-white/40">
              Passwort
            </label>
            <div className="relative">
              <input
                data-testid="admin-login-password"
                type={showPw ? "text" : "password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="sol-input pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                aria-label={showPw ? "Passwort verbergen" : "Passwort anzeigen"}
                className="absolute right-1 top-1/2 -translate-y-1/2 text-white/45 hover:text-white"
              >
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={busy}
            className="w-full btn-bronze rounded-full py-3.5 text-sm font-medium disabled:opacity-60"
          >
            {busy ? "Anmelden…" : "Anmelden"}
          </button>
        </form>

        <Link
          to="/"
          className="mt-8 inline-flex items-center gap-2 text-white/45 hover:text-white text-sm font-body"
        >
          <ArrowLeft size={15} /> Zur Startseite
        </Link>
      </div>
    </main>
  );
}

// ----------------- Protected route wrapper -----------------
function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <main className="bg-[var(--sol-bg)] min-h-screen flex items-center justify-center text-white/40 font-body">
        Lade…
      </main>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
}

// ----------------- Dashboard -----------------
export function AdminDashboard() {
  const { authedAxios } = useAuth();
  const [posts, setPosts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("blog");

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const ax = authedAxios();
      const [b, c, l] = await Promise.all([
        ax.get("/admin/blog"),
        ax.get("/contacts"),
        ax.get("/funnel-leads"),
      ]);
      setPosts(b.data);
      setContacts(c.data);
      setLeads(l.data);
    } catch (e) {
      toast.error("Konnte Admin-Daten nicht laden.");
    } finally {
      setLoading(false);
    }
  }, [authedAxios]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ax = authedAxios();
        const [b, c, l] = await Promise.all([
          ax.get("/admin/blog"),
          ax.get("/contacts"),
          ax.get("/funnel-leads"),
        ]);
        if (cancelled) return;
        setPosts(b.data);
        setContacts(c.data);
        setLeads(l.data);
      } catch {
        if (!cancelled) toast.error("Konnte Admin-Daten nicht laden.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedAxios]);

  const remove = async (slug) => {
    if (!window.confirm(`Beitrag "${slug}" wirklich löschen?`)) return;
    try {
      await authedAxios().delete(`/admin/blog/${slug}`);
      toast.success("Beitrag gelöscht.");
      load();
    } catch {
      toast.error("Löschen fehlgeschlagen.");
    }
  };

  return (
    <Protected>
      <main data-testid="admin-dashboard-page" className="bg-[var(--sol-bg)] min-h-screen text-white">
        <AdminHeader />
        <div className="max-w-6xl mx-auto px-6 md:px-10 pt-32 pb-24">
          <p className="eyebrow">Dashboard</p>
          <h1 className="font-display text-4xl md:text-5xl font-extralight tracking-tight mt-4">
            Verwaltung
          </h1>

          <div className="mt-10 flex gap-2 border-b hairline">
            {[
              { id: "blog", label: `Blog (${posts.length})` },
              { id: "contacts", label: `Kontakt (${contacts.length})` },
              { id: "leads", label: `Solar-Check Leads (${leads.length})` },
            ].map((t) => (
              <button
                key={t.id}
                data-testid={`admin-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`px-5 py-3 text-sm font-body transition border-b-2 -mb-px ${
                  tab === t.id
                    ? "border-[var(--sol-bronze)] text-white"
                    : "border-transparent text-white/45 hover:text-white"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="mt-12 text-white/40 font-body">Lade…</div>
          ) : tab === "blog" ? (
            <section className="mt-10">
              <div className="flex justify-end mb-6">
                <Link
                  to="/admin/blog/new"
                  data-testid="admin-new-post"
                  className="btn-bronze rounded-full px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2"
                >
                  <Plus size={16} /> Neuer Beitrag
                </Link>
              </div>
              <div className="space-y-3">
                {posts.map((p) => (
                  <div
                    key={p.slug}
                    data-testid={`admin-post-row-${p.slug}`}
                    className="bg-[var(--sol-surface)] border border-white/8 rounded-2xl p-5 flex flex-col md:flex-row md:items-center gap-3 md:gap-6"
                  >
                    <div className="flex-1">
                      <p className="text-[10px] tracking-[0.22em] uppercase text-[var(--sol-bronze-light)]">
                        {p.category} {p.published ? "" : " · Entwurf"}
                      </p>
                      <h3 className="font-display text-lg text-white mt-1">{p.title}</h3>
                      <p className="text-xs text-white/40 mt-1 font-body">/{p.slug}</p>
                    </div>
                    <div className="flex gap-2">
                      <Link
                        to={`/admin/blog/edit/${p.slug}`}
                        data-testid={`admin-edit-${p.slug}`}
                        className="btn-ghost rounded-full px-4 py-2 inline-flex items-center gap-2 text-sm"
                      >
                        <PenSquare size={14} /> Bearbeiten
                      </Link>
                      <button
                        onClick={() => remove(p.slug)}
                        data-testid={`admin-delete-${p.slug}`}
                        className="text-red-400/80 hover:text-red-300 rounded-full px-3 py-2 inline-flex items-center gap-1.5 text-sm border border-red-400/20 hover:border-red-400/50 transition"
                      >
                        <Trash2 size={14} /> Löschen
                      </button>
                    </div>
                  </div>
                ))}
                {posts.length === 0 && (
                  <div className="text-white/40 font-body">Noch keine Beiträge.</div>
                )}
              </div>
            </section>
          ) : tab === "contacts" ? (
            <section className="mt-10 space-y-3" data-testid="admin-contacts-list">
              {contacts.length === 0 && (
                <p className="text-white/40 font-body">Noch keine Kontaktanfragen.</p>
              )}
              {contacts.map((c) => (
                <div
                  key={c.id}
                  className="bg-[var(--sol-surface)] border border-white/8 rounded-2xl p-5"
                >
                  <div className="flex flex-wrap justify-between gap-3">
                    <div>
                      <h4 className="font-display text-lg text-white">{c.name}</h4>
                      <p className="text-xs text-white/45 font-body">
                        {c.email} {c.phone && `· ${c.phone}`}
                      </p>
                    </div>
                    <span className="text-[11px] text-white/40 font-body">
                      {new Date(c.created_at).toLocaleString("de-CH")}
                    </span>
                  </div>
                  <p className="font-body text-white/70 mt-4 whitespace-pre-wrap text-sm">
                    {c.message}
                  </p>
                </div>
              ))}
            </section>
          ) : (
            <section className="mt-10 space-y-3" data-testid="admin-leads-list">
              {leads.length === 0 && (
                <p className="text-white/40 font-body">Noch keine Solar-Check Leads.</p>
              )}
              {leads.map((l) => (
                <div
                  key={l.id}
                  className="bg-[var(--sol-surface)] border border-white/8 rounded-2xl p-5"
                >
                  <div className="flex flex-wrap justify-between gap-3">
                    <div>
                      <h4 className="font-display text-lg text-white">{l.name}</h4>
                      <p className="text-xs text-white/45 font-body">
                        {l.email} {l.phone && `· ${l.phone}`}{" "}
                        {l.plz && `· ${l.plz} ${l.ort || ""}`}
                      </p>
                    </div>
                    <span className="text-[11px] text-white/40 font-body">
                      {new Date(l.created_at).toLocaleString("de-CH")}
                    </span>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3 mt-4 text-xs text-white/65 font-body">
                    {Object.entries(l.answers || {}).map(([k, v]) => (
                      <div key={k} className="flex justify-between border-b border-white/5 py-1">
                        <span className="text-white/40">{k}</span>
                        <span>{String(v ?? "-")}</span>
                      </div>
                    ))}
                  </div>
                  {l.estimate && (
                    <div className="mt-4 grid sm:grid-cols-2 gap-3 text-xs">
                      {Object.entries(l.estimate).map(([k, v]) => (
                        <div
                          key={k}
                          className="flex justify-between bg-[var(--sol-bronze)]/5 border border-[var(--sol-bronze)]/20 rounded-lg px-3 py-2"
                        >
                          <span className="text-[var(--sol-bronze-light)]">{k}</span>
                          <span className="text-white">{String(v ?? "-")}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </section>
          )}
        </div>
      </main>
    </Protected>
  );
}

// ----------------- Editor -----------------
export function AdminBlogEditor() {
  const { slug: paramSlug } = useParams();
  const navigate = useNavigate();
  const { authedAxios } = useAuth();
  const isEdit = Boolean(paramSlug);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [form, setForm] = useState({
    slug: "",
    title: "",
    excerpt: "",
    content: "",
    category: "Photovoltaik",
    author: "Solanova Team",
    cover_url: "",
    read_minutes: 4,
    published: true,
  });

  useEffect(() => {
    if (!isEdit) return;
    authedAxios()
      .get(`/admin/blog/${paramSlug}`)
      .then(({ data }) => setForm(data))
      .catch(() => toast.error("Beitrag konnte nicht geladen werden."))
      .finally(() => setLoading(false));
  }, [paramSlug, authedAxios]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const slugify = (s) =>
    s
      .toLowerCase()
      .replace(/[äöüß]/g, (c) => ({ ä: "ae", ö: "oe", ü: "ue", ß: "ss" }[c] || c))
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isEdit) {
        await authedAxios().put(`/admin/blog/${paramSlug}`, {
          title: form.title,
          excerpt: form.excerpt,
          content: form.content,
          category: form.category,
          author: form.author,
          cover_url: form.cover_url,
          read_minutes: parseInt(form.read_minutes, 10),
          published: form.published,
        });
        toast.success("Beitrag aktualisiert.");
      } else {
        const payload = { ...form, slug: form.slug || slugify(form.title) };
        await authedAxios().post(`/admin/blog`, payload);
        toast.success("Beitrag erstellt.");
      }
      navigate("/admin");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Speichern fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Protected>
      <main data-testid="admin-editor-page" className="bg-[var(--sol-bg)] min-h-screen text-white">
        <AdminHeader />
        <div className="max-w-4xl mx-auto px-6 md:px-10 pt-32 pb-24">
          <Link
            to="/admin"
            className="text-white/60 hover:text-white text-sm inline-flex items-center gap-2 font-body"
          >
            <ArrowLeft size={15} /> Zurück
          </Link>
          <h1 className="font-display text-3xl md:text-4xl font-extralight tracking-tight mt-6">
            {isEdit ? "Beitrag bearbeiten" : "Neuer Beitrag"}
          </h1>

          {loading ? (
            <div className="mt-12 text-white/40 font-body">Lade…</div>
          ) : (
            <form
              onSubmit={save}
              className="mt-10 bg-[var(--sol-surface)] border border-white/8 rounded-3xl p-6 md:p-10 space-y-6"
              data-testid="admin-editor-form"
            >
              <div className="grid md:grid-cols-2 gap-x-8 gap-y-2">
                <div className="md:col-span-2">
                  <label className="text-[11px] tracking-[0.22em] uppercase text-white/40">Titel</label>
                  <input
                    required
                    data-testid="editor-title"
                    className="sol-input"
                    value={form.title}
                    onChange={(e) => set("title", e.target.value)}
                  />
                </div>
                {!isEdit && (
                  <div className="md:col-span-2">
                    <label className="text-[11px] tracking-[0.22em] uppercase text-white/40">
                      Slug (URL) <span className="text-white/30">– leer = automatisch</span>
                    </label>
                    <input
                      data-testid="editor-slug"
                      className="sol-input"
                      value={form.slug}
                      onChange={(e) =>
                        set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))
                      }
                      placeholder={slugify(form.title || "")}
                    />
                  </div>
                )}
                <div>
                  <label className="text-[11px] tracking-[0.22em] uppercase text-white/40">Kategorie</label>
                  <input
                    required
                    data-testid="editor-category"
                    className="sol-input"
                    value={form.category}
                    onChange={(e) => set("category", e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-[11px] tracking-[0.22em] uppercase text-white/40">Autor</label>
                  <input
                    required
                    data-testid="editor-author"
                    className="sol-input"
                    value={form.author}
                    onChange={(e) => set("author", e.target.value)}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-[11px] tracking-[0.22em] uppercase text-white/40">Cover-Bild URL</label>
                  <input
                    data-testid="editor-cover"
                    className="sol-input"
                    value={form.cover_url || ""}
                    onChange={(e) => set("cover_url", e.target.value)}
                    placeholder="https://…"
                  />
                </div>
                <div>
                  <label className="text-[11px] tracking-[0.22em] uppercase text-white/40">
                    Lesezeit (Min.)
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={60}
                    data-testid="editor-read-minutes"
                    className="sol-input"
                    value={form.read_minutes}
                    onChange={(e) => set("read_minutes", parseInt(e.target.value || "1", 10))}
                  />
                </div>
                <div className="flex items-center gap-3 mt-7">
                  <input
                    id="published"
                    type="checkbox"
                    data-testid="editor-published"
                    checked={form.published}
                    onChange={(e) => set("published", e.target.checked)}
                    className="accent-[var(--sol-bronze)] w-4 h-4"
                  />
                  <label htmlFor="published" className="text-sm text-white/70 font-body">
                    Veröffentlicht
                  </label>
                </div>
              </div>

              <div>
                <label className="text-[11px] tracking-[0.22em] uppercase text-white/40">
                  Kurzbeschreibung
                </label>
                <textarea
                  required
                  data-testid="editor-excerpt"
                  rows={2}
                  className="sol-input"
                  value={form.excerpt}
                  onChange={(e) => set("excerpt", e.target.value)}
                />
              </div>

              <div>
                <label className="text-[11px] tracking-[0.22em] uppercase text-white/40">
                  Inhalt (Markdown)
                </label>
                <textarea
                  required
                  data-testid="editor-content"
                  rows={20}
                  className="sol-input font-mono text-sm"
                  value={form.content}
                  onChange={(e) => set("content", e.target.value)}
                  placeholder="## Überschrift&#10;&#10;Absatz…&#10;&#10;- Liste Punkt 1&#10;- Liste Punkt 2"
                />
                <p className="text-[11px] text-white/35 mt-2 font-body">
                  Unterstützt: ## Überschriften · **Fett** · `code` · - Listen · | Tabellen |
                </p>
              </div>

              <div className="flex items-center justify-end pt-4">
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="editor-save"
                  className="btn-bronze rounded-full px-7 py-3.5 inline-flex items-center gap-2 text-sm font-medium disabled:opacity-60"
                >
                  <Save size={16} /> {busy ? "Speichern…" : "Speichern"}
                </button>
              </div>
            </form>
          )}
        </div>
      </main>
    </Protected>
  );
}
